import { useState, useEffect, useCallback } from 'react';
/**
 * Keyboard navigation hook for search results.
 */
export function useKeyboardNav(results, onSelect, onClose) {
    const [activeIndex, setActiveIndex] = useState(-1);
    // Reset active index when results change
    useEffect(() => {
        setActiveIndex(results.length > 0 ? 0 : -1);
    }, [results]);
    const handleKeyDown = useCallback((event) => {
        // Escape always closes the modal, even when there are no results.
        if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
            return;
        }
        if (results.length === 0)
            return;
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setActiveIndex(prev => prev < results.length - 1 ? prev + 1 : 0);
                break;
            case 'ArrowUp':
                event.preventDefault();
                setActiveIndex(prev => prev > 0 ? prev - 1 : results.length - 1);
                break;
            case 'Enter':
                if (activeIndex >= 0 && activeIndex < results.length) {
                    event.preventDefault();
                    onSelect(results[activeIndex]);
                }
                break;
        }
    }, [results, activeIndex, onSelect, onClose]);
    // Add global keydown listener
    useEffect(() => {
        const isTextInput = (target) => {
            if (!target || !(target instanceof HTMLElement))
                return false;
            const tag = target.tagName.toLowerCase();
            if (tag === 'textarea' || target.isContentEditable)
                return true;
            // Only treat text-like inputs as blocking (not checkboxes, radios, etc.)
            if (tag === 'input') {
                const type = target.type.toLowerCase();
                return type === 'text' || type === 'search' || type === 'email' || type === '' || type === 'password';
            }
            return false;
        };
        const handler = (event) => {
            // Navigation keys always fire even inside the text input so the user can
            // move through results while typing without leaving the search field.
            const isNavKey = event.key === 'ArrowUp' || event.key === 'ArrowDown'
                || event.key === 'Enter' || event.key === 'Escape';
            if (isNavKey || !isTextInput(event.target)) {
                handleKeyDown(event);
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [handleKeyDown]);
    return {
        activeIndex,
        setActiveIndex,
    };
}
